import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/terminal.css';

const Terminal = () => {
  const [lines, setLines] = useState<{
    type: string;
    content: string;
    path?: string;
    visible: boolean;
  }[]>([
    { type: 'title', content: 'Building the Future of Web3, AI and Privacy', visible: false },
    { type: 'motto', content: 'We share the passion for progress with ZK as the engine for innovation', visible: false },
    { type: 'command', content: 'zk-verify --security-proofs', visible: false },
    { type: 'output', content: 'Initializing security verification...', visible: false },
    { type: 'output', content: '[1/5] Verifying zero-knowledge proofs...', visible: false },
    { type: 'output', content: '[2/5] Checking cryptographic primitives...', visible: false },
    { type: 'output', content: '[3/5] Validating privacy guarantees...', visible: false },
    { type: 'output', content: '[4/5] Testing security parameters...', visible: false },
    { type: 'success', content: '✓ All security proofs verified successfully', visible: false },
    { type: 'options', content: 'Select an option:', visible: false },
    { type: 'option', content: '1. Explore Solutions', path: '/services/technology', visible: false },
    { type: 'option', content: '2. Learn More', path: '/ecosystem', visible: false }
  ]);
  
  const [isComplete, setIsComplete] = useState(false);
  const navigate = useNavigate();

  const showNextLine = useCallback(() => {
    setLines(currentLines => {
      const nextLineIndex = currentLines.findIndex(line => !line.visible);
      if (nextLineIndex === -1) {
        setIsComplete(true);
        return currentLines;
      }
      return currentLines.map((line, index) => 
        index === nextLineIndex ? { ...line, visible: true } : line
      );
    });
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      showNextLine();
    }, 100);

    return () => clearInterval(timer);
  }, [showNextLine]);

  const handleOptionClick = (path?: string) => {
    if (path && isComplete) {
      navigate(path);
    }
  };

  return (
    <div className="w-full">
      <div className="terminal w-full max-w-none">
        <div className="terminal-dots">
          <div className="terminal-dot terminal-dot-red"></div>
          <div className="terminal-dot terminal-dot-yellow"></div>
          <div className="terminal-dot terminal-dot-green"></div>
        </div>
        <div className="terminal-content h-[520px]">
          <AnimatePresence initial={false}>
            {lines.map((line, index) => (
              line.visible && (
                <motion.div
                  key={index}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                  className={`terminal-line ${
                    line.type === 'title' ? 'text-3xl font-bold mb-4 gradient-text' :
                    line.type === 'motto' ? 'text-xl text-slate-400 mb-8' :
                    ''
                  }`}
                >
                  {line.type === 'command' ? (
                    <>
                      <span className="terminal-prompt">$</span>
                      <span className="terminal-command"> {line.content}</span>
                    </>
                  ) : line.type === 'option' ? (
                    <button
                      onClick={() => handleOptionClick(line.path)}
                      className={`terminal-option ${isComplete ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                      disabled={!isComplete}
                    >
                      <span className="option-content">{line.content}</span>
                    </button>
                  ) : line.type === 'success' ? (
                    <span className="terminal-success">{line.content}</span>
                  ) : line.type === 'output' ? (
                    <span className="terminal-output">{line.content}</span>
                  ) : (
                    line.content
                  )}
                </motion.div>
              )
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Terminal;